import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { api, errorMessage, post } from "@/lib/api";
import { useSession } from "@/lib/session";
import { money } from "@/lib/types";
import type { Listing } from "@/lib/types";
import type { Product } from "@/lib/shop";

import { Icon } from "./ui";

export function ListingCard({ listing }: { listing: Listing | Product }) {
  const client = useQueryClient();
  const session = useSession();
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const product = "variants" in listing ? listing : null;
  const favoritePath = product ? "/shop/favorites/" + product.id + "/" : "/favorites/" + listing.id + "/";
  const detailPath = product ? "/boutique/$slug" : "/annonces/$slug";
  const favorite = useMutation({
    mutationFn: () => listing.is_favorite
      ? api(favoritePath, { method: "DELETE" })
      : post(favoritePath),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ["listings"] }); },
    onError: (error) => setMessage(errorMessage(error)),
  });
  return <article className="listing-card">
    <div className="card-image-wrap">
      <Link to={detailPath} params={{ slug: listing.slug }} aria-label={listing.title}>
        {listing.photos[0] ? <img src={listing.photos[0].image} alt={listing.title} loading="lazy" width="600" height="720" />
          : <div className="photo-placeholder"><span>revi.</span></div>}
      </Link>
      {listing.status === "sold" ? <span className="card-tag">Une nouvelle histoire ✧</span>
        : ["reserved", "out_of_stock"].includes(listing.status) ? <span className="card-tag">{listing.status_label}</span>
        : listing.condition === "new" && <span className="card-tag">Jamais utilisé</span>}
      <button className={"favorite-button " + (listing.is_favorite ? "is-favorite" : "")}
        aria-label={listing.is_favorite ? "Retirer des favoris" : "Ajouter aux favoris"}
        aria-pressed={listing.is_favorite} disabled={favorite.isPending} onClick={() => {
          if (!session.data?.user) { void navigate({ to: "/connexion" }); return; }
          favorite.mutate();
        }}><Icon name="heart" size={19} /></button>
    </div>
    <div className="card-meta"><span>{listing.category_name}</span><span>{listing.location.split(",")[0]}</span></div>
    <Link to={detailPath} params={{ slug: listing.slug }} className="card-title">{listing.title}</Link>
    <p className="card-price">{money(listing.price)} <span>{listing.condition === "excellent" ? "Excellent état" : listing.condition === "new" ? "Neuf" : "Bon état"}</span></p>
    {message && <p className="field-error" role="alert">{message}</p>}
  </article>;
}
